import { Box, DialogActions, DialogContent, SxProps } from '@mui/material';
import React from 'react';
import useSnackbar from '@hooks/useSnackbar';
import GenericLoader from '@molecules/Loaders/GenericLoader';
import { ModalContext } from '@organisms/Modals/ModalContext';
import ModalHeader from '@organisms/Modals/ModalHeader';

function BaseModal({
  children,
  titre,
  actions,
  isLoading = false,
  isError = false,
  sx,
  testId,
}: Readonly<{
  titre?: React.ReactNode;
  children?: React.ReactNode;
  actions?: React.ReactNode;
  isLoading?: boolean;
  isError?: boolean;
  dark?: boolean;
  sx?: SxProps;
  testId?: string;
}>) {
  const { closeModal } = React.useContext(ModalContext);
  const { displayError } = useSnackbar();
  const [isScrolled, setIsScrolled] = React.useState(false);
  const contentRef = React.useRef<HTMLDivElement>(null);

  const handleScroll = React.useCallback(() => {
    if (contentRef.current) {
      setIsScrolled(contentRef.current.scrollTop > 0);
    }
  }, []);

  if (isLoading) {
    return <GenericLoader />;
  }

  if (isError) {
    displayError('Impossible de charger le popup');
    closeModal();
  }

  return (
    <Box
      data-testid={testId}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: 'background.paper',
        overflow: 'hidden',
        ...sx,
      }}
    >
      {titre && (
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            transition: 'box-shadow 0.2s ease',
            boxShadow: isScrolled ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
          }}
        >
          <ModalHeader>{titre}</ModalHeader>
        </Box>
      )}
      <DialogContent
        ref={contentRef}
        onScroll={handleScroll}
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 3,
          '&::-webkit-scrollbar': {
            width: 8,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'grey.100',
            borderRadius: 4,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'grey.300',
            borderRadius: 4,
            '&:hover': {
              backgroundColor: 'grey.400',
            },
          },
        }}
      >
        {children}
      </DialogContent>
      {actions && (
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
          }}
        >
          {actions}
        </DialogActions>
      )}
    </Box>
  );
}

export default BaseModal;
